import React, { useState, useEffect } from 'react'
import { Link, useParams, useLocation } from 'react-router-dom'
import {
  getCenterPublic,
  listCenterMediaPublic,
  listCenterEventsPublic,
  listCenterOffersPublic
} from '../../services/centers'
import {
  BuildingOffice2Icon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  PhotoIcon,
  VideoCameraIcon,
  CalendarIcon,
  TagIcon,
  ClockIcon,
  MapIcon
} from '@heroicons/react/24/outline'

const CenterProfilePage = () => {
  const { centerId } = useParams()
  const location = useLocation()
  const [center, setCenter] = useState(null)
  const [media, setMedia] = useState([])
  const [events, setEvents] = useState([])
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(null) // null means showing media (default)

  useEffect(() => {
    // Determine active tab from URL
    const path = location.pathname
    if (path.includes('/events')) {
      setActiveTab('events')
    } else if (path.includes('/offers')) {
      setActiveTab('offers')
    } else {
      setActiveTab(null) // null = show media by default
    }
  }, [location.pathname])

  useEffect(() => {
    fetchCenterData()
  }, [centerId])

  const fetchCenterData = async () => {
    setLoading(true)
    
    // Fetch center profile
    const centerRes = await getCenterPublic(centerId)
    if (centerRes.status === 200) {
      setCenter(centerRes.data)
    }

    // Fetch media
    const mediaRes = await listCenterMediaPublic(centerId)
    if (mediaRes.status === 200) {
      setMedia(mediaRes.data || [])
    }

    // Fetch events
    const eventsRes = await listCenterEventsPublic(centerId)
    if (eventsRes.status === 200) {
      setEvents(eventsRes.data || [])
    }

    // Fetch offers
    const offersRes = await listCenterOffersPublic(centerId)
    if (offersRes.status === 200) {
      setOffers(offersRes.data || [])
    }

    setLoading(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-16 w-16 animate-spin rounded-full border-4 border-solid border-cyan-600 border-r-transparent"></div>
          <p className="mt-4 text-slate-600 text-lg">Loading center profile...</p>
        </div>
      </div>
    )
  }

  if (!center) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <BuildingOffice2Icon className="mx-auto h-24 w-24 text-slate-300" />
          <h3 className="mt-6 text-2xl font-semibold text-slate-900">Center not found</h3>
          <Link to="/centers" className="mt-4 inline-block text-cyan-600 hover:text-cyan-700 font-semibold">
            Browse all centers
          </Link>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'offers', label: 'Offers', path: `/centers/${centerId}/offers`, count: offers.length },
    { id: 'events', label: 'Events', path: `/centers/${centerId}/events`, count: events.length }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 font-sans">
      {/* Center Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center shadow-xl shrink-0">
              <BuildingOffice2Icon className="w-12 h-12 text-cyan-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-4">{center.organization}</h1>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {center.state && (
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="w-5 h-5 text-cyan-200" />
                    <span className="text-cyan-50">
                      {center.localGovernment ? `${center.localGovernment}, ` : ''}{center.state}
                    </span>
                  </div>
                )}
                {center.phoneNumber && (
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="w-5 h-5 text-cyan-200" />
                    <span className="text-cyan-50">{center.phoneNumber}</span>
                  </div>
                )}
                {center.email && (
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="w-5 h-5 text-cyan-200" />
                    <span className="text-cyan-50">{center.email}</span>
                  </div>
                )}
                {center.address && (
                  <div className="flex items-center gap-2">
                    <MapIcon className="w-5 h-5 text-cyan-200" />
                    <span className="text-cyan-50">{center.address}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            <Link
              to={`/centers/${centerId}`}
              className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                activeTab === null
                  ? 'text-cyan-600 border-cyan-600'
                  : 'text-slate-600 border-transparent hover:text-slate-900 hover:border-slate-300'
              }`}
            >
              Media <span className="ml-2 opacity-60">({media.length})</span>
            </Link>
            {tabs.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-cyan-600 border-cyan-600'
                    : 'text-slate-600 border-transparent hover:text-slate-900 hover:border-slate-300'
                }`}
              >
                {tab.label} <span className="ml-2 opacity-60">({tab.count})</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === null && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Media Gallery</h2>
            {media.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                <PhotoIcon className="mx-auto h-20 w-20 text-slate-300" />
                <h3 className="mt-4 text-xl font-semibold text-slate-900">No media yet</h3>
                <p className="mt-2 text-slate-600">This center hasn't uploaded any media</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {media.map(item => (
                  <div key={item._id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-lg transition-all">
                    <div className="aspect-square bg-slate-100 relative overflow-hidden">
                      {item.type === 'IMAGE' ? (
                        <img
                          src={item.url}
                          alt={item.caption || 'Media'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" dy="10.5" font-weight="bold" x="50%" y="50%" text-anchor="middle"%3EFailed to load%3C/text%3E%3C/svg%3E'
                          }}
                        />
                      ) : (
                        <div className="relative w-full h-full">
                          <video
                            src={item.url}
                            className="w-full h-full object-cover"
                            controls
                            preload="metadata"
                            onError={(e) => {
                              console.error('Video failed to load:', item.url)
                            }}
                          >
                            Your browser does not support the video tag.
                          </video>
                          <div className="absolute top-3 left-3 pointer-events-none">
                            <VideoCameraIcon className="w-8 h-8 text-white drop-shadow-lg" />
                          </div>
                        </div>
                      )}
                    </div>
                    {item.caption && (
                      <div className="p-4">
                        <p className="text-sm text-slate-600 line-clamp-2">{item.caption}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'events' && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Upcoming Events</h2>
            {events.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                <CalendarIcon className="mx-auto h-20 w-20 text-slate-300" />
                <h3 className="mt-4 text-xl font-semibold text-slate-900">No events available</h3>
                <p className="mt-2 text-slate-600">This center doesn't have any upcoming events</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map(event => (
                  <Link
                    key={event._id}
                    to={`/events/${event._id}`}
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-xl transition-all group"
                  >
                    {event.coverImage && (
                      <div className="h-48 bg-slate-100 overflow-hidden">
                        <img
                          src={event.coverImage}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-cyan-600 transition-colors">
                        {event.title}
                      </h3>
                      <div className="space-y-2 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <ClockIcon className="w-4 h-4 text-slate-400" />
                          <span>{new Date(event.startsAt).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPinIcon className="w-4 h-4 text-slate-400" />
                          <span>{event.venue}</span>
                        </div>
                      </div>
                      {event.description && (
                        <p className="mt-3 text-sm text-slate-600 line-clamp-2">
                          {event.description}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'offers' && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Special Offers</h2>
            {offers.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
                <TagIcon className="mx-auto h-20 w-20 text-slate-300" />
                <h3 className="mt-4 text-xl font-semibold text-slate-900">No offers available</h3>
                <p className="mt-2 text-slate-600">This center doesn't have any active offers</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {offers.map(offer => (
                  <Link
                    key={offer._id}
                    to={`/offers/${offer._id}`}
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-xl transition-all group"
                  >
                    {offer.images && offer.images.length > 0 && (
                      <div className="h-48 bg-slate-100 overflow-hidden">
                        <img
                          src={offer.images[0]}
                          alt={offer.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-cyan-600 transition-colors">
                        {offer.title}
                      </h3>
                      <div className="flex items-baseline gap-3 mb-3">
                        <span className="text-2xl font-bold text-green-600">
                          ₦{offer.discountedPrice?.toLocaleString()}
                        </span>
                        <span className="text-lg text-slate-400 line-through">
                          ₦{offer.normalPrice?.toLocaleString()}
                        </span>
                      </div>
                      {offer.description && (
                        <p className="text-sm text-slate-600 line-clamp-2">
                          {offer.description}
                        </p>
                      )}
                      <div className="mt-4 flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                          {offer.remaining} / {offer.quantity} available
                        </span>
                        <span className="text-green-600 font-semibold">
                          Save {Math.round(((offer.normalPrice - offer.discountedPrice) / offer.normalPrice) * 100)}%
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CenterProfilePage

